/**
 * TrainingLmsPage.jsx · v2.7.64+E49 · Learning Management Center (LMS)
 * /admin/training-center/lms
 * ────────────────────────────────────────────────────────────
 * Learning portal hosting training tracks for Drivers, Safety and Staff.
 * Course cards with per-user completion state; opening a module renders
 * the material and lets the logged-in user mark it complete — progress
 * persists in `lms_progress` keyed to their identity.
 */
import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Circle, GraduationCap, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AgentBadge, AudienceChip, TrainingHeader, TrainingMarkdown,
} from "./trainingShared";

function ProgressBar({ completed, total, testid }) {
  const pct = total ? Math.round((completed / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <div className="flex-1 h-2 rounded-full bg-zinc-800 overflow-hidden">
        <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[11px] font-mono text-zinc-400">{completed}/{total} · {pct}%</span>
    </div>
  );
}

export default function TrainingLmsPage() {
  const [tracks, setTracks] = useState([]);
  const [overall, setOverall] = useState({ completed: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [openModule, setOpenModule] = useState(null);
  const [saving, setSaving] = useState(false);
  // v2.7.65+E50 · on-behalf recording (supervisor tier)
  const [driversList, setDriversList] = useState([]);
  const [behalfId, setBehalfId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/training/lms/courses");
      setTracks(data.tracks || []);
      setOverall(data.overall || { completed: 0, total: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load courses");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Driver roster for classroom sign-offs (best-effort — non-supervisors
  // simply get a 403 toast when they try to record)
  useEffect(() => {
    adminApi.get("/admin/drivers", { params: { role: "driver" } })
      .then((r) => setDriversList(Array.isArray(r.data) ? r.data : []))
      .catch(() => setDriversList([]));
  }, []);

  const recordOnBehalf = async () => {
    if (!openModule || !behalfId) return;
    setSaving(true);
    try {
      await adminApi.post("/admin/training/lms/progress", {
        content_id: openModule.id, is_completed: true, user_id: behalfId,
      });
      const d = driversList.find((x) => x.id === behalfId);
      toast.success(`Recorded for ${[d?.first_name, d?.last_name].filter(Boolean).join(" ") || "driver"}: ${openModule.title}`);
      setBehalfId("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to record — supervisor tier required");
    } finally {
      setSaving(false);
    }
  };

  const toggleComplete = async (mod, nextState) => {
    setSaving(true);
    try {
      await adminApi.post("/admin/training/lms/progress", {
        content_id: mod.id, is_completed: nextState,
      });
      toast.success(nextState ? `Completed: ${mod.title}` : `Marked incomplete: ${mod.title}`);
      setOpenModule((m) => (m && m.id === mod.id ? { ...m, is_completed: nextState } : m));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save progress");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="training-lms-page">
      <TrainingHeader
        Icon={GraduationCap}
        title="Learning Management Center"
        subtitle="Training tracks for Drivers, Safety and internal Staff · completion tracked per user"
        right={
          <button onClick={load} className="p-2 rounded-md border border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-colors"
                  data-testid="lms-refresh-btn" title="Refresh">
            <RefreshCw className="h-4 w-4" />
          </button>
        }
      />

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-8">
        {/* Overall progress */}
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl px-5 py-4" data-testid="lms-overall-card">
          <div className="text-[11px] font-mono text-zinc-500 uppercase mb-2">Your overall progress</div>
          <ProgressBar completed={overall.completed} total={overall.total} testid="lms-overall-progress" />
        </div>

        {loading && <div className="text-zinc-500 text-sm py-8 text-center" data-testid="lms-loading">Loading courses…</div>}
        {!loading && tracks.length === 0 && (
          <div className="text-zinc-500 text-sm py-8 text-center" data-testid="lms-empty">
            No training modules yet — add some through the Knowledge Management Center.
          </div>
        )}

        {tracks.map((t) => (
          <div key={t.section_group} data-testid={`lms-track-${t.chapter_number}`}>
            <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
              <h2 className="text-sm font-black text-emerald-300/90 uppercase tracking-wide">
                Track {t.chapter_number} — {t.section_group}
              </h2>
              <div className="w-52">
                <ProgressBar completed={t.completed} total={t.total} testid={`lms-track-progress-${t.chapter_number}`} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {t.modules.map((m) => (
                <button key={m.id} onClick={() => setOpenModule(m)}
                  className={`text-left rounded-xl px-4 py-4 border transition-colors ${
                    m.is_completed
                      ? "bg-emerald-500/5 border-emerald-500/40 hover:border-emerald-400/70"
                      : "bg-zinc-900/60 border-zinc-800 hover:border-emerald-500/40"
                  }`}
                  data-testid={`lms-module-card-${m.slug}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-bold text-zinc-100">{m.title}</div>
                    {m.is_completed
                      ? <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0" data-testid={`lms-module-done-${m.slug}`} />
                      : <Circle className="h-5 w-5 text-zinc-600 flex-shrink-0" />}
                  </div>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <AudienceChip audience={m.target_audience} />
                    {m.is_agent_generated && <AgentBadge />}
                    <span className="text-[10px] text-zinc-500">{m.word_count} words</span>
                  </div>
                  {m.is_completed && m.completed_at && (
                    <div className="text-[10px] font-mono text-emerald-400/70 mt-2">
                      ✓ completed {new Date(m.completed_at).toLocaleString()}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Module reader dialog */}
      <Dialog open={!!openModule} onOpenChange={(o) => { if (!o) setOpenModule(null); }}>
        <DialogContent className="bg-zinc-950 border-zinc-700 text-zinc-100 sm:max-w-2xl max-h-[85vh] overflow-y-auto"
                       data-testid="lms-module-dialog">
          <DialogHeader>
            <DialogTitle className="text-emerald-300 flex items-center gap-2">
              <GraduationCap className="h-5 w-5" />
              {openModule?.title}
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              {openModule?.section_group} · <AudienceChip audience={openModule?.target_audience} />
            </DialogDescription>
          </DialogHeader>
          <TrainingMarkdown>{openModule?.body_content}</TrainingMarkdown>
          <div className="pt-3 border-t border-zinc-800 flex items-center justify-between gap-3">
            <span className="text-[11px] text-zinc-500">
              {openModule?.is_completed ? "You've completed this module." : "Read the material, then mark it complete."}
            </span>
            <button
              onClick={() => toggleComplete(openModule, !openModule.is_completed)}
              disabled={saving}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-bold border transition-colors disabled:opacity-50 ${
                openModule?.is_completed
                  ? "border-zinc-700 text-zinc-300 hover:border-zinc-500"
                  : "bg-emerald-500/15 border-emerald-500/50 text-emerald-200 hover:bg-emerald-500/25"
              }`}
              data-testid="lms-mark-complete-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {openModule?.is_completed ? "Mark Incomplete" : "Mark Complete"}
            </button>
          </div>
          {/* v2.7.65+E50 · supervisors record classroom completions on behalf of drivers */}
          <div className="pt-3 border-t border-zinc-800/60 flex items-center gap-2 flex-wrap" data-testid="lms-onbehalf-row">
            <span className="text-[10px] font-mono text-zinc-500 uppercase">Record for a driver (supervisor):</span>
            <select
              value={behalfId}
              onChange={(e) => setBehalfId(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 rounded-md px-2 py-1.5 text-xs text-zinc-100 outline-none focus:border-emerald-500/60"
              data-testid="lms-onbehalf-select">
              <option value="">— select driver —</option>
              {driversList.map((d) => (
                <option key={d.id} value={d.id}>
                  {[d.first_name, d.last_name].filter(Boolean).join(" ") || d.email || d.id}
                </option>
              ))}
            </select>
            <button
              onClick={recordOnBehalf}
              disabled={!behalfId || saving}
              className="px-3 py-1.5 rounded-md border border-emerald-500/40 text-emerald-200 text-[11px] font-bold hover:bg-emerald-500/15 transition-colors disabled:opacity-50"
              data-testid="lms-onbehalf-record-btn">
              Record completion
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
